import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MLoader from "@/components/ui/atoms/MLoader";
import MHeader from "@/components/ui/organisms/MHeader";
import MInfoCard from "@/components/ui/atoms/MInfoCard";
import { IconCircleX } from "@tabler/icons-react";
import { useProfileField } from "@/hooks/useHealthProfile";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { MDialog } from "@/components/ui/atoms/MDialog";
import EmptyAfterDeleteDialog from "@/components/ui/molecules/EmptyAfterDeleteDialog";
import { MedicalDetailType } from "@/types/health-profile";
import { useTranslation } from "react-i18next";
import { getMemberInfo } from "@/hooks/useMembers";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";
import { useNavigationSource } from "@/hooks/useNavigationSource";

const Allergies: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Navigation source
  const { navigateBackIfExists } = useNavigationSource();
  // Get member info from Redux store
  const memberInfo = getMemberInfo();
  const memberId = memberInfo?.id || "";
  const {
    fieldValue: allergies = [],
    loading,
    saveField,
  } = useProfileField(memberId, MedicalDetailType.ALLERGIES, []);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEmptyDialog, setShowEmptyDialog] = useState(false);
  const [allergyToDelete, setAllergyToDelete] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { showToast } = useToast();

  // Track page load once
  useEffect(() => {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
      screenName: ANALYTICS_SCREEN_NAMES.ALLERGIES_HOME,
    });
  }, []);

  // Save allergies to profile
  const saveAllergies = async (allergies: string[]) => {
    await saveField(allergies);
  };

  const handleDelete = async () => {
    if (!allergyToDelete) return;
    setDeleteLoading(true);
    try {
      const newAllergies = selectedAllergies.filter(
        (a) => a !== allergyToDelete
      );
      await saveAllergies(newAllergies);
      if (newAllergies.length === 0) {
        setShowEmptyDialog(true);
      }
    } catch (error) {
      showToast({
        message: t("errors.toast"),
        variant: "error",
      });
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
      setAllergyToDelete(null);
    }
  };

  // Get selected allergies from profile or empty array
  const selectedAllergies = Array.isArray(allergies) ? allergies : [];

  // Show loader while fetching
  if (loading) {
    return <MLoader />;
  }

  return (
    <div className="profile-page-root">
      <MHeader
        module="allergies"
        memberInfo={memberInfo}
        onAdd={() => navigate("/profile/health-profile/allergies/add-allergy")}
        showGradient={true}
        onBack={() => {
          const didNavigate = navigateBackIfExists();
          if (!didNavigate) {
            navigate("/profile/health-profile");
          }
        }}
      />

      <div className="profile-flex-col-gap">
        {selectedAllergies.map((allergy) => (
          <MInfoCard
            key={allergy.type + ":" + allergy.name}
            primaryLabel={allergy.name}
            variant="actionsMenu"
            isAllergy={true}
            actions={[
              {
                id: "delete",
                label: t("common.delete"),
                icon: <IconCircleX size={20} />,
                onClick: () => {
                  setAllergyToDelete(allergy);
                  setShowDeleteModal(true);
                },
                className: "delete",
              },
            ]}
          />
        ))}
      </div>

      <MDialog
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setAllergyToDelete(null);
        }}
        title={t("allergies.delete.title")}
        description={t(
          `allergies.delete.description${
            memberInfo?.onBehalf ? "_on_behalf" : ""
          }`
        )}
        primaryCtaText={t("common.yes")}
        onPrimaryCta={handleDelete}
        isPrimaryCtaLoading={deleteLoading}
        secondaryCtaText={t("common.no")}
        onSecondaryCta={() => {
          setShowDeleteModal(false);
          setAllergyToDelete(null);
        }}
      />
      <EmptyAfterDeleteDialog
        isOpen={showEmptyDialog}
        onConfirm={() => {
          setShowEmptyDialog(false);
          const didNavigate = navigateBackIfExists();
          if (!didNavigate) navigate("/profile/health-profile");
        }}
      />
    </div>
  );
};

export default Allergies;
